import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../SEO';
import { 
  Maximize2, Facebook, Twitter, Linkedin, 
  Instagram, Ruler, Weight, TreeDeciduous,
  ChevronRight, ChevronLeft, Heart, Share2
} from 'lucide-react';
import './ProductDetail.css';
import { Home,  Slash } from 'lucide-react';
import '../Breadcrumb/ArtisanBreadcrumb.css';
const API_URL = 'https://api.sohanihandicrafts.com:5010';
const ArtisanBreadcrumb = ({ 
  title = "Description Page", 
  bgImage, 
  paths = [] 
}) => {

  // Default paths if none provided
  const breadcrumbs = paths.length > 0 ? paths : [
    { name: "Home", link: "/" },
    { name: "Description Page", link: "/" }
  ];

  return (
    <div className="bc-art-wrapper">
      
      {/* 1. DYNAMIC BACKGROUND IMAGE (Parallax feel) */}
      <div 
        className="bc-art-bg"
        style={{ 
          backgroundImage: `url(${bgImage || ''})` 
        }}
      >
        <div className="bc-art-overlay"></div>
      </div>

      {/* 2. ANIMATED DECORATION RING (Subtle rotation) */}
      <motion.div 
        animate={{ rotate: 360 }}
        transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
        className="bc-art-deco-ring"
      >
        <svg viewBox="0 0 100 100">
           <path d="M50 0C22.4 0 0 22.4 0 50s22.4 50 50 50 50-22.4 50-50S77.6 0 50 0zm0 90C27.9 90 10 72.1 10 50S27.9 10 50 10s40 17.9 40 40-17.9 40-40 40z" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.3" strokeDasharray="4 4"/>
        </svg>
      </motion.div>

      {/* 3. CONTENT CONTAINER */}
      <div className="bc-art-content">
        
        {/* Animated Title */}
        <motion.h1 
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="bc-art-title"
        >
          {title}
        </motion.h1>

        {/* The Glassmorphic Breadcrumb Bar */}
        <motion.nav 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bc-art-bar"
        >
          <ul className="bc-art-list">
            
            {/* Home Icon Always First */}
            <li>
              <a href="/" className="bc-art-home-icon">
                <Home size={16} />
              </a>
            </li>

            {/* Loop through paths */}
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;

              return (
                <li key={index} className="bc-art-item">
                  
                  {/* Separator */}
                  <span className="bc-art-sep">
                    <ChevronRight size={12} strokeWidth={3} />
                  </span>

                  {/* Link or Active Text */}
                  {isLast ? (
                    <span className="bc-art-text active">
                      {crumb.name}
                    </span>
                  ) : (
                    <a href={crumb.link} className="bc-art-text link">
                      {crumb.name}
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </motion.nav>
      </div>
    </div>
  );
};
const ProductDetail = () => {
  const { id } = useParams(); // Get Item ID from URL
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [activeImg, setActiveImg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(false);
  const [sliderIndex, setSliderIndex] = useState(0);

  // 1. Fetch Main Product Details
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/item/${id}`);
        setProduct(res.data);
        setActiveImg(`${API_URL}/${res.data.image}`); // Set initial image
        
        // 2. Fetch Related Products (same category)
        if (res.data.category) {
          const relatedRes = await axios.get(`${API_URL}/api/item/category/${res.data.category._id}`);
          // Filter out current product from related list
          setRelatedProducts(relatedRes.data.filter(item => item._id !== id));
        }
      } catch (err) {
        console.error("Error fetching product:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
    window.scrollTo(0, 0); // Scroll to top on load
  }, [id]);

  // Slider Logic
  const nextSlide = () => {
    if (sliderIndex < relatedProducts.length - 4) {
      setSliderIndex(sliderIndex + 1);
    }
  };

  const prevSlide = () => {
    if (sliderIndex > 0) {
      setSliderIndex(sliderIndex - 1);
    }
  };

  if (loading) return <div className="pdp-loader">Loading Artisan Piece...</div>;
  if (!product) return <div className="pdp-loader">Product not found.</div>;

  // Prepare Specs Array based on Backend Data
const productSpecs = product ? [
    { 
        icon: <Ruler size={18} />, 
        label: "Dimensions", 
        value: product.dimensions // Dynamic from DB
    },
    { 
        icon: <Weight size={18} />, 
        label: "Weight", 
        value: product.weight // Dynamic from DB
    },
    { 
        icon: <TreeDeciduous size={18} />, 
        label: "Material", 
        value: product.material // Dynamic from DB
    }
] : [];

  return (
    <>
    <SEO 
  title={`${product.name} | Hand-Finished Artisan Piece`}
  description={`Buy ${product.name}. Crafted with ${product.material}. Dimensions: ${product.dimensions}. Authentic Jodhpur handicraft with superior durability and finish. Request a quote today.`}
  keywords={`${product.name}, ${product.material} furniture, handcrafted ${product.category?.name}, furniture sku ${product.sku}, jodhpur furniture price, high end wood decor`}
  image={`${API_URL}/${product.image}`}
  url={`/product-detail/${product._id}`}
/>
      <ArtisanBreadcrumb  />
    <div className="pdp-wrapper">
      <div className="pdp-container">
        
        {/* 1. BREADCRUMBS */}
        <nav className="pdp-breadcrumbs">
          <Link to="/">Home</Link> <ChevronRight size={14} /> 
          <Link to={`/products/${product.category?._id}`}>{product.category?.name || 'Collection'}</Link> <ChevronRight size={14} /> 
          <span>{product.name}</span>
        </nav>

        <div className="pdp-main-grid">
          {/* Gallery Column */}
          <div className="pdp-gallery-column">
            <div className="pdp-main-image-container" onMouseEnter={() => setZoom(true)} onMouseLeave={() => setZoom(false)}>
              <AnimatePresence mode="wait">
                <motion.img 
                  key={activeImg} src={activeImg} 
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className={`pdp-main-image ${zoom ? 'is-zoomed' : ''}`} alt={product.name}
                />
              </AnimatePresence>
              <div className="pdp-image-hint"><Maximize2 size={20} /></div>
            </div>
            
            {/* Thumbnails (If you have multiple images in backend, map them here. Currently using single image) */}
            <div className="pdp-thumbnails">
                <div className={`pdp-thumb-item active`}>
                  <img src={`${API_URL}/${product.image}`} alt="thumbnail" />
                </div>
            </div>
          </div>

          {/* Info Column */}
          <div className="pdp-info-column">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="pdp-info-sticky">
              <h1 className="pdp-title">{product.name}</h1>
              <div className="pdp-divider"></div>
              
              <ul className="pdp-specs-list">
                {productSpecs.map((spec, i) => (
                  <li key={i}>
                    <span className="pdp-spec-icon">{spec.icon}</span>
                    <span className="pdp-spec-label">{spec.label} — </span>
                    <span className="pdp-spec-value">{spec.value}</span>
                  </li>
                ))}
              </ul>

              <div className="pdp-actions">
                <Link to="/contact" className="pdp-quote-btn">Add to Quote</Link>
                {/* <button className="pdp-wish-btn"><Heart size={20} /></button> */}
              </div>

              <div className="pdp-meta">
                <p><strong>SKU:</strong> <span>{product.sku}</span></p>
                <p><strong>Category:</strong> <span>{product.category?.name}</span></p>
              </div>

              <div className="pdp-share">
                <span className="pdp-share-label">Share:</span>
                <div className="pdp-social-icons">
                  <a href="#"><Facebook size={18} /></a><a href="#"><Twitter size={18} /></a>
                  <a href="#"><Linkedin size={18} /></a><a href="#"><Instagram size={18} /></a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* 4. DYNAMIC RELATED PRODUCTS SLIDER */}
        {relatedProducts.length > 0 && (
          <section className="pdp-related">
            <div className="pdp-related-header">
              <h2 className="pdp-section-title">Related Masterpieces</h2>
              <div className="pdp-slider-nav">
                <button 
                  onClick={prevSlide} 
                  className={`pdp-nav-btn ${sliderIndex === 0 ? 'disabled' : ''}`}
                >
                  <ChevronLeft size={24} />
                </button>
                <button 
                  onClick={nextSlide} 
                  className={`pdp-nav-btn ${sliderIndex >= relatedProducts.length - 4 ? 'disabled' : ''}`}
                >
                  <ChevronRight size={24} />
                </button>
              </div>
            </div>

            <div className="pdp-slider-container">
              <motion.div 
                className="pdp-slider-track"
                animate={{ x: `-${sliderIndex * (100 / 4)}%` }}
                transition={{ type: "spring", stiffness: 100, damping: 20 }}
              >
                {relatedProducts.map((item) => (
                  <div key={item._id} className="pdp-slider-item">
                    <div className="pdp-related-card" onClick={() => navigate(`/product-detail/${item._id}`)}>
                      <div className="pdp-related-img">
                        <img src={`${API_URL}/${item.image}`} alt={item.name} />
                        <div className="pdp-related-overlay">
                          <button>View Piece</button>
                        </div>
                      </div>
                      <div className="pdp-related-info">
                        <span className="pdp-related-tag">SKU: {item.sku}</span>
                        <h3>{item.name}</h3>
                        <Link to="/contact" className="pdp-related-action">Add to Quote</Link>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </section>
        )}

      </div>
    </div>
    </>
  );
};

export default ProductDetail;